import React, { ReactNode, useMemo, useState } from "react";
import {
  Table,
  Empty,
  Tooltip,
  Button,
  Space,
  Switch,
  Tag,
  TableColumnType,
  Row,
  Drawer,
  Form,
  Typography,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  DislikeOutlined,
  DownloadOutlined,
  EyeOutlined,
  LikeOutlined,
  MinusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { JsonReportView } from "api/generated";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { formatErrors, hasNoErrors, transformErrorStringToList } from "./JsonReportUtils";
import JsonReportDifferencesTable from "./JsonReportDifferencesTable";
import { UseTableSelectionReturn } from "hooks/commons/useTableSelection";
import { useIsMobile } from "hooks/useIsMobile";
import { useSelfPagination } from "hooks/commons/usePaginationTable";
import type { FixedType } from "rc-table/lib/interface";
import type { DataIndex } from "rc-table/lib/interface";

interface IconColorProps {
  icon: ReactNode;
  color: string;
}

interface StatusConfig {
  statusColumnTitle?: string;
  isSent?: ReactNode;
  notSent?: ReactNode;
  render?: boolean;
}

interface JsonReportsTableProps {
  data: JsonReportView[];
  loading?: boolean;
  // Props para modo edición
  onDownload?: (reportName: string) => void;
  onView?: (reportName: string) => void;
  onToggleSent?: (checked: boolean, record: JsonReportView) => void;
  // Props de configuración
  mode?: "edit" | "readonly";
  // Prop para personalizar estados en modo lectura
  statusConfig?: StatusConfig;
  selection?: UseTableSelectionReturn;
  fixedColumns?: DataIndex<JsonReportView>[];
}

const ALLOWED_DIFF = 1;
const WARN_DIFF = 2.9;
const DEFAULT_VALUE = 0;
const MAX_JSON_SIZE_TO_BE_PREVIEWED = 10;

// Configuración por defecto para los estados
const defaultStatusConfig: StatusConfig = {
  isSent: <Tag color="green">Enviado</Tag>,
  notSent: <Tag color="red">No enviado</Tag>,
  statusColumnTitle: "Enviado al SAT",
  render: true,
};

const JsonReportsTable: React.FC<JsonReportsTableProps> = ({
  data,
  loading = false,
  onDownload,
  onView,
  onToggleSent,
  mode = "edit",
  statusConfig = defaultStatusConfig,
  selection,
  fixedColumns = ["creationDate"],
}) => {
  const isReadOnly = mode === "readonly";
  const [activeReport, setActiveReport] = useState<JsonReportView>();
  const [openInconsistencies, setOpenInconsistencies] = useState(false);
  const [openDifferences, setOpenDifferences] = useState(false);
  const { isMobile } = useIsMobile();
  const paginationConfig = useSelfPagination(data);

  const sentConfig = useMemo(() => {
    return {
      ...defaultStatusConfig,
      ...statusConfig,
    };
  }, [statusConfig]);

  function getFixedColumn(
    dataIndex: DataIndex<JsonReportView>,
  ): FixedType | undefined {
    if (isMobile || dataIndex === undefined) {
      return undefined;
    }
    return fixedColumns.includes(dataIndex) ? "left" : undefined;
  }

  const dateColumn: TableColumnType<JsonReportView> = {
    title: "Fecha Reporte",
    dataIndex: "creationDate",
    width: "4rem",
    align: "center",
    render: (date: string, record: JsonReportView) =>
      record.reportType === "DIARIO"
        ? dayjs(date).format("DD [de] MMMM YYYY")
        : dayjs(date).format("MMMM [de] YYYY"),
  };

  const distributionColumn: TableColumnType<JsonReportView> = {
    title: "Punto distribución",
    width: "5rem",
    align: "center",
    dataIndex: "distributionName",
    render: (text: string, record: JsonReportView) => (
      <div style={{ justifyItems: "center" }}>
        <Row>{text}</Row>
        <Row>
          <Tag color="geekblue">{record.senerLicense}</Tag>
        </Row>
      </div>
    ),
  };

  const nameColumn: TableColumnType<JsonReportView> = {
    title: "Nombre Reporte",
    dataIndex: "reportName",
    width: "15rem",
    align: "center",
    render: (text: string) => text?.split("/").at(-1) || text,
  };

  const typeColumn: TableColumnType<JsonReportView> = {
    title: "Tipo Reporte",
    width: "3rem",
    align: "center",
    dataIndex: "reportType",
    render: (text: string) => (
      <Tag color={text === "DIARIO" ? "blue" : "cyan"}>{text}</Tag>
    ),
  };

  const actionsColumn: TableColumnType<JsonReportView> = {
    title: "Acciones",
    width: "3rem",
    align: "center",
    render: (_, record: JsonReportView) => (
      <Space>
        {onDownload && (
          <Tooltip title="Descargar Reporte" placement="top">
            <Button
              icon={<DownloadOutlined />}
              onClick={() => onDownload(record.reportName || "")}
            />
          </Tooltip>
        )}
        {onView && (
          <Tooltip
            title={`${
              (record.sizeJsonMB ?? 0) > MAX_JSON_SIZE_TO_BE_PREVIEWED
                ? "Reporte muy extenso para ser visualizado, opte por descargarlo"
                : "Ver Reporte"
            }`}
            placement="top"
          >
            <Button
              icon={<EyeOutlined />}
              disabled={
                (record.sizeJsonMB ?? 0) > MAX_JSON_SIZE_TO_BE_PREVIEWED
              }
              onClick={() => onView(record.reportName || "")}
            />
          </Tooltip>
        )}
      </Space>
    ),
  };

  const inconsistencies: string[] = useMemo(() => {
    if (activeReport && activeReport.errors) {
      return transformErrorStringToList(activeReport.errors);
    }
    return [];
  }, [activeReport]);

  function showInconsistencies(record: JsonReportView) {
    setActiveReport(record);
    setOpenInconsistencies(true);
  }

  function showDifferencesTable(record: JsonReportView) {
    setActiveReport(record);
    setOpenDifferences(true);
  }

  function getDifferenceAttributes(record: JsonReportView): IconColorProps {
    const {
      salesDifferencePercentage = DEFAULT_VALUE,
      receptionDifferencePercentage = DEFAULT_VALUE,
      differenceInventoryPercentage = DEFAULT_VALUE,
    } = record;

    const maxDiff = Math.max(
      salesDifferencePercentage || DEFAULT_VALUE,
      receptionDifferencePercentage || DEFAULT_VALUE,
      differenceInventoryPercentage || DEFAULT_VALUE,
    );

    const options = {
      like: { icon: <LikeOutlined />, color: "green" },
      line: { icon: <MinusOutlined />, color: "orange" },
      dislike: { icon: <DislikeOutlined />, color: "red" },
    };

    if (maxDiff < ALLOWED_DIFF) return options.like;
    if (maxDiff < WARN_DIFF) return options.line;
    return options.dislike;
  }

  const differencesColum: TableColumnType<JsonReportView> = {
    title: "Detalles",
    width: "3rem",
    align: "center",
    render: (_, record: JsonReportView) => {
      const noErrors = hasNoErrors(record.errors);
      const isMonthly = record.reportType !== "DIARIO";
      const { icon, color } = getDifferenceAttributes(record);
      return (
        <Space>
          <Tooltip
            title={noErrors ? "Sin inconsistencias" : "Inconsistencias"}
            placement="top"
          >
            <Button
              icon={noErrors ? <CheckCircleOutlined /> : <WarningOutlined />}
              onClick={() => {
                if (!noErrors) showInconsistencies(record);
              }}
              disabled={noErrors}
              variant="filled"
              color={noErrors ? "green" : "orange"}
            />
          </Tooltip>
          {isMonthly && (
            <Tooltip title="Diferencias" placement="top">
              <Button
                icon={icon}
                style={{
                  backgroundColor: color,
                  borderColor: color,
                  color: "white",
                }}
                onClick={() => showDifferencesTable(record)}
              />
            </Tooltip>
          )}
        </Space>
      );
    },
  };

  const sentStatusColumn: TableColumnType<JsonReportView> = {
    title: statusConfig.statusColumnTitle,
    dataIndex: "alreadySent",
    width: "5rem",
    align: "center",
    render: (value: boolean, record: JsonReportView) => {
      if (isReadOnly) {
        // En modo lectura, usa la configuración personalizada o los tags por defecto
        return value ? sentConfig.isSent : sentConfig.notSent;
      } else {
        const ControlledSwitch = () => {
          const [checked, setChecked] = React.useState(value);
          React.useEffect(() => {
            setChecked(value);
          }, [value]);
          const handleChange = (newValue: boolean) => {
            setChecked(newValue);
            onToggleSent?.(newValue, record);
          };
          return <Switch checked={checked} onChange={handleChange} />;
        };
        return <ControlledSwitch />;
      }
    },
  };

  const columns = [
    dateColumn,
    distributionColumn,
    nameColumn,
    typeColumn,
    ...(!isReadOnly && (onDownload || onView) ? [actionsColumn] : []),
    differencesColum,
    ...(sentConfig.render ? [sentStatusColumn] : []),
  ].map((column) => {
    return { ...column, fixed: getFixedColumn(column.dataIndex) };
  });

  function handleClose() {
    setActiveReport(undefined);
    setOpenInconsistencies(false);
    setOpenDifferences(false);
  }

  return (
    <div>
      <Table
        columns={columns}
        loading={loading}
        showHeader={data.length === 0 ? false : true}
        bordered
        scroll={{
          x: 1500,
          y: "calc(100vh - 600px)",
        }}
        rowSelection={selection?.rowSelection}
        rowKey="idReport"
        dataSource={data}
        locale={{
          emptyText: <Empty description="Sin registros" />,
        }}
        pagination={paginationConfig}
      />
      <Drawer
        title="Validación del Reporte Json"
        height="auto"
        width={isMobile ? "100%" : "50%"}
        placement="right"
        onClose={handleClose}
        open={openInconsistencies && inconsistencies.length > 0}
        styles={{
          mask: {
            backdropFilter: "blur(10px)",
          },
        }}
        footer={<Button onClick={handleClose}>Cerrar</Button>}
      >
        <Form layout="vertical">
          <Row gutter={24}>
            <ResponsiveCol size="xl">
              <Typography.Text strong>
                Punto distribución: {activeReport?.distributionName}
              </Typography.Text>
            </ResponsiveCol>
            <ResponsiveCol size="xl">
              <Typography.Text>
                Fecha del Reporte:{" "}
                {dayjs(activeReport?.creationDate).format("YYYY/MM/DD")}
              </Typography.Text>
            </ResponsiveCol>
            <ResponsiveCol size="xl">
              Tipo Reporte:
              <Tag
                color={activeReport?.reportType === "DIARIO" ? "blue" : "cyan"}
              >
                {activeReport?.reportType}
              </Tag>
            </ResponsiveCol>
            <Divider />

            {inconsistencies.length > 0 && (
              <ResponsiveCol size="xl">
                {formatErrors(inconsistencies)}
              </ResponsiveCol>
            )}
            {inconsistencies.length === 0 && (
              <ResponsiveCol size="xl">
                <span>
                  La validación del reporte Json no encontró problemas.
                </span>
              </ResponsiveCol>
            )}
          </Row>
        </Form>
      </Drawer>

      {activeReport && (
        <JsonReportDifferencesTable
          onCancel={handleClose}
          open={openDifferences}
          record={activeReport}
        />
      )}
    </div>
  );
};

export default JsonReportsTable;
